import { supabase } from '../lib/supabase';
import { logAuditEvent } from './auditLogService';

/**
 * Contract Management Service
 * Handles contract creation, escrow, time tracking, milestones, and lifecycle management
 * 
 * Payment Model: Stripe Destination Charges
 *   - Client pays → funds go directly to expert's Connect account
 *   - Platform commission collected via application_fee_amount
 *   - Expert's Connect account has manual payouts (escrow simulation)
 *   - Bank payout triggered after work approval + KYC verification
 */

/**
 * Create contract with escrow hold
 */
export async function createContract(contractData) {
  try {
    const {
      clientId,
      expertId,
      caseId,
      contractType,
      title,
      description,
      deliverables,
      hourlyRate,
      budgetCap,
      totalContractValue,
      startDate,
      deadline,
      milestones
    } = contractData;

    // Create contract
    const { data: contract, error: contractError } = await supabase
      ?.from('marketplace_contracts')
      ?.insert({
        client_id: clientId,
        expert_id: expertId,
        case_id: caseId,
        contract_type: contractType,
        title,
        description,
        deliverables,
        hourly_rate: hourlyRate,
        budget_cap: budgetCap,
        total_contract_value: totalContractValue,        escrow_amount: totalContractValue || budgetCap || 0,        start_date: startDate,
        deadline,
        status: 'offered'
      })
      ?.select()
      ?.single();

    if (contractError) throw contractError;

    // For milestone contracts, create milestones
    if (contractType === 'milestone' && milestones?.length > 0) {
      const milestoneInserts = milestones?.map((m, index) => ({
        contract_id: contract?.id,
        title: m?.title,
        description: m?.description,
        amount: m?.amount,
        due_date: m?.dueDate,
        display_order: index
      }));

      const { error: milestoneError } = await supabase
        ?.from('contract_milestones')
        ?.insert(milestoneInserts);

      if (milestoneError) throw milestoneError;
    }

    logAuditEvent({ actionType: 'contract.created', actionDescription: `Contract created: ${contract?.title}`, entityType: 'contract', entityId: contract?.id, metadata: { contractType: contract?.contract_type }, severity: 'info' }).catch(console.error);

    // Fire-and-forget: notify the expert of the new contract offer
    supabase?.functions?.invoke('send-contract-update-notification', {
      body: { contractId: contract?.id, eventType: 'offered' }
    }).catch(() => {});

    return { success: true, contract };
  } catch (error) {
    console.error('Create contract error:', error);
    return { success: false, error: error?.message };
  }
}

/**
 * Create escrow Payment Intent hold (Destination Charge)
 * Funds go directly to the expert's Connect account with platform commission
 * deducted as application_fee_amount. Expert's manual payout schedule means
 * funds stay in their Connect balance until released.
 */
export async function createEscrowHold(contractId, amount, clientStripeCustomerId, paymentMethodId) {
  try {
    // Validate inputs
    if (!contractId) throw new Error('Contract ID is required');
    if (!amount || amount <= 0) throw new Error('Amount must be greater than 0');
    if (amount < 1) throw new Error('Minimum escrow amount is $1.00');
    if (!clientStripeCustomerId) throw new Error('Stripe customer ID is required');
    if (!paymentMethodId) throw new Error('Payment method is required');

    // Call Edge Function to create escrow hold securely
    const { data, error } = await supabase?.functions?.invoke('create-escrow-hold', {
      body: {
        contractId,
        amount,
        clientStripeCustomerId,
        paymentMethodId,
      }
    });

    if (error) {
      const msg = await extractFunctionError(error);
      throw new Error(msg);
    }
    if (data?.error) throw new Error(data.error);

    logAuditEvent({ actionType: 'payment.escrow_held', actionDescription: `Escrow held for contract`, entityType: 'contract', entityId: contractId, metadata: { amount }, severity: 'warning' }).catch(console.error);
    return { success: true, paymentIntentId: data?.paymentIntentId, clientSecret: data?.clientSecret };
  } catch (error) {
    console.error('Create escrow hold error:', error);
    return { success: false, error: error?.message };
  }
}

/**
 * Fetch the client's saved payment methods from user_payment_methods table.
 * Returns { methods: [...], stripeCustomerId: '...' } or { success: false }
 */
export async function getClientPaymentMethods(userId) {
  try {
    const { data, error } = await supabase
      ?.from('user_payment_methods')
      ?.select('stripe_payment_method_id, stripe_customer_id, card_brand, card_last4, card_exp_month, card_exp_year, is_default')
      ?.eq('user_id', userId)
      ?.order('is_default', { ascending: false })
      ?.order('created_at', { ascending: false });

    if (error) throw error;

    const methods = (data || []).map((row) => ({
      id: row.stripe_payment_method_id,
      brand: row.card_brand,
      last4: row.card_last4,
      exp_month: row.card_exp_month,
      exp_year: row.card_exp_year,
      is_default: row.is_default,
    }));

    const stripeCustomerId = data?.[0]?.stripe_customer_id ?? null;

    return { success: true, methods, stripeCustomerId };
  } catch (error) {
    console.error('Get client payment methods error:', error);
    return { success: false, error: error?.message, methods: [], stripeCustomerId: null };
  }
}

/**
 * Extract a human-readable error message from a Supabase FunctionsHttpError.
 * When an edge function returns a 4xx status, `data` is null and `error` is a
 * FunctionsHttpError whose `.context` holds the actual response body.
 */
async function extractFunctionError(error) {
  if (error?.context) {
    try {
      const body = await error.context.json();
      if (body?.error) return body.error;
    } catch (_) { /* ignore parse errors */ }
  }
  return error?.message || 'Unknown error';
}

export async function releaseEscrow(contractId, approvedAmount) {
  try {
    const body = { contractId };
    if (approvedAmount != null) body.approvedAmount = approvedAmount;
    console.log('[releaseEscrow] Invoking edge function with body:', JSON.stringify(body));

    const { data, error } = await supabase?.functions?.invoke('release-escrow', {
      body
    });

    console.log('[releaseEscrow] Edge function response — data:', JSON.stringify(data), 'error:', error?.message ?? null);

    if (error) {
      const msg = await extractFunctionError(error);
      console.error('[releaseEscrow] Edge function error:', msg);
      throw new Error(msg);
    }
    if (data?.error) {
      console.error('[releaseEscrow] data.error:', data.error);
      throw new Error(data.error);
    }

    console.log('[releaseEscrow] SUCCESS — verifiedEscrowStatus:', data?.verifiedEscrowStatus);
    logAuditEvent({ actionType: 'payment.escrow_released', actionDescription: `Escrow released to expert`, entityType: 'contract', entityId: contractId, severity: 'warning' }).catch(console.error);
    return { success: true };
  } catch (error) {
    console.error('[releaseEscrow] CATCH:', error?.message);
    return { success: false, error: error?.message };
  }
}

export async function refundEscrow(contractId, amount) {
  try {
    const body = { contractId };
    if (amount !== undefined && amount !== null) body.amount = amount;
    const { data, error } = await supabase?.functions?.invoke('refund-escrow', {
      body
    });
    if (error) {
      const msg = await extractFunctionError(error);
      throw new Error(msg);
    }
    if (data?.error) throw new Error(data.error);
    logAuditEvent({ actionType: 'payment.escrow_refunded', actionDescription: `Escrow refunded to client`, entityType: 'contract', entityId: contractId, severity: 'warning' }).catch(console.error);
    return { success: true };
  } catch (error) {
    console.error('Refund escrow error:', error);
    return { success: false, error: error?.message };
  }
}

/**
 * Accept contract
 */
export async function acceptContract(contractId) {
  try {
    const { data, error } = await supabase
      ?.from('marketplace_contracts')
      ?.update({
        status: 'accepted',
        accepted_at: new Date()?.toISOString()
      })
      ?.eq('id', contractId)
      ?.eq('status', 'offered')
      ?.select()
      ?.single();

    if (error) throw error;
    if (!data) throw new Error('Contract cannot be accepted — it is no longer in "offered" state');

    logAuditEvent({ actionType: 'contract.accepted', actionDescription: `Contract accepted`, entityType: 'contract', entityId: contractId, severity: 'info' }).catch(console.error);

    // Fire-and-forget: notify the client that the expert accepted
    supabase?.functions?.invoke('send-contract-update-notification', {
      body: { contractId, eventType: 'accepted', recipientRole: 'client' }
    }).catch(() => {});

    return { success: true, contract: data };
  } catch (error) {
    console.error('Accept contract error:', error);
    return { success: false, error: error?.message };
  }
}

/**
 * Start contract (move to in_progress)
 */
export async function startContract(contractId) {
  try {
    const { data, error } = await supabase
      ?.from('marketplace_contracts')
      ?.update({
        status: 'in_progress',
        start_date: new Date()?.toISOString()?.split('T')?.[0]
      })
      ?.eq('id', contractId)
      ?.eq('status', 'accepted')
      ?.select()
      ?.single();

    if (error) throw error;
    if (!data) throw new Error('Contract cannot be started — it is not in "accepted" state');

    logAuditEvent({ actionType: 'contract.started', actionDescription: `Contract started`, entityType: 'contract', entityId: contractId, severity: 'info' }).catch(console.error);
    return { success: true, contract: data };
  } catch (error) {
    console.error('Start contract error:', error);
    return { success: false, error: error?.message };
  }
}

/**
 * Complete contract
 */
export async function submitContractForReview(contractId) {
  try {
    const { data, error } = await supabase
      ?.from('marketplace_contracts')
      ?.update({ status: 'pending_review' })
      ?.eq('id', contractId)
      ?.eq('status', 'in_progress')
      ?.select()
      ?.single();

    if (error) throw error;
    if (!data) throw new Error('Contract cannot be submitted for review — it is not in "in progress" state');

    logAuditEvent({ actionType: 'contract.submitted_for_review', actionDescription: `Contract submitted for client review`, entityType: 'contract', entityId: contractId, severity: 'info' }).catch(console.error);

    // Fire-and-forget: notify the client that work is ready for review
    supabase?.functions?.invoke('send-contract-update-notification', {
      body: { contractId, eventType: 'pending_review', recipientRole: 'client' }
    }).catch(() => {});

    return { success: true, contract: data };
  } catch (error) {
    console.error('Submit for review error:', error);
    return { success: false, error: error?.message };
  }
}

export async function completeContract(contractId, approvedAmount) {
  try {
    // 1. Check contract is in reviewable state and get escrow info
    const { data: existing } = await supabase
      ?.from('marketplace_contracts')
      ?.select('status, escrow_status, stripe_payment_intent_id')
      ?.eq('id', contractId)
      ?.single();

    console.log('[completeContract] existing contract state:', JSON.stringify(existing));
    if (!existing) throw new Error('Contract not found');
    if (existing.status === 'completed') throw new Error('Contract is already completed');
    if (existing.status === 'cancelled') throw new Error('Cannot complete a cancelled contract');
    if (!['pending_review', 'in_progress', 'accepted'].includes(existing.status)) {
      throw new Error(`Contract cannot be completed from "${existing.status}" state`);
    }

    // 2. Release escrow to expert BEFORE marking complete.
    if (existing?.escrow_status === 'held') {
      console.log('[completeContract] Calling releaseEscrow with contractId:', contractId, 'approvedAmount:', approvedAmount);
      const releaseResult = await releaseEscrow(contractId, approvedAmount);
      console.log('[completeContract] releaseEscrow result:', JSON.stringify(releaseResult));
      if (!releaseResult?.success) {
        throw new Error(releaseResult?.error || 'Failed to release escrow to expert');
      }

      // Verify escrow_status actually changed before proceeding
      const { data: afterRelease } = await supabase
        ?.from('marketplace_contracts')
        ?.select('escrow_status, payout_status')
        ?.eq('id', contractId)
        ?.single();
      console.log('[completeContract] AFTER releaseEscrow, DB read-back:', JSON.stringify(afterRelease));
      if (afterRelease?.escrow_status !== 'released') {
        console.error('[completeContract] !!!! escrow_status is STILL', afterRelease?.escrow_status, 'after releaseEscrow returned success');
      }
    } else {
      console.log('[completeContract] Skipping releaseEscrow — escrow_status:', existing?.escrow_status, '(not held)');
    }

    // 3. Mark contract as completed (with status guard to prevent races)
    const { data, error } = await supabase
      ?.from('marketplace_contracts')
      ?.update({
        status: 'completed',
        completed_at: new Date()?.toISOString()
      })
      ?.eq('id', contractId)
      ?.in('status', ['pending_review', 'in_progress', 'accepted'])
      ?.select()
      ?.single();

    console.log('[completeContract] Final update result:', JSON.stringify({ data: data ? { id: data.id, status: data.status, escrow_status: data.escrow_status } : null, error: error?.message }));
    if (error) throw error;
    if (!data) throw new Error('Contract could not be completed — status may have changed');

    logAuditEvent({ actionType: 'contract.completed', actionDescription: `Contract completed`, entityType: 'contract', entityId: contractId, severity: 'info' }).catch(console.error);

    // Fire-and-forget: notify the expert by email
    supabase?.functions?.invoke('send-contract-update-notification', {
      body: { contractId, eventType: 'completed' }
    }).catch(() => {});

    return { success: true, contract: data };
  } catch (error) {
    console.error('Complete contract error:', error);
    return { success: false, error: error?.message };
  }
}

/**
 * Cancel contract.
 *
 * All cancellation policy logic (who gets paid, how much) is enforced
 * server-side in the cancel-contract Edge Function and cannot be tampered
 * with by client-side code. This function is a thin proxy.
 *
 * @param {string} contractId
 * @param {string} reason
 */
export async function cancelContract(contractId, reason) {
  try {
    if (!reason?.trim()) throw new Error('Cancellation reason is required');

    const { data, error } = await supabase.functions.invoke('cancel-contract', {
      body: { contractId, reason },
    });

    if (error) return { success: false, error: error.message };
    if (data?.error) return { success: false, error: data.error };

    return { success: true, cancelledBy: data.cancelledBy, expertReceives: data.expertReceives };
  } catch (error) {
    console.error('Cancel contract error:', error);
    return { success: false, error: error?.message };
  }
}

/**
 * File a dispute on a contract.
 * Marks the contract as 'disputed' and notifies all admins by email.
 */
export async function disputeContract(contractId, reason) {
  try {
    if (!reason?.trim()) throw new Error('Dispute reason is required');

    const { data, error } = await supabase
      ?.from('marketplace_contracts')
      ?.update({
        status: 'disputed',
        cancellation_reason: reason,
        updated_at: new Date()?.toISOString()
      })
      ?.eq('id', contractId)
      ?.not('status', 'in', '("completed","cancelled","disputed")')
      ?.select()
      ?.single();

    if (error) throw error;
    if (!data) throw new Error('Contract could not be disputed — status may have changed');

    logAuditEvent({
      actionType: 'contract.disputed',
      actionDescription: `Contract disputed: ${reason}`,
      entityType: 'contract',
      entityId: contractId,
      metadata: { reason },
      severity: 'warning'
    }).catch(console.error);

    supabase?.functions?.invoke('send-dispute-notification', {
      body: { contractId, reason }
    }).catch((err) => console.warn('send-dispute-notification failed:', err));

    return { success: true, contract: data };
  } catch (error) {
    console.error('Dispute contract error:', error);
    return { success: false, error: error?.message };
  }
}

/**
 * Get user contracts
 */
export async function getUserContracts(userId, role = 'client') {
  try {
    console.log('🔍 getUserContracts called with:', { userId, role });
    let queryColumn = 'client_id';
    let queryValue = userId;

    // For experts, we need to get their expert_profile.id first
    if (role === 'expert') {
      console.log('🔍 Looking up expert_profile for user_id:', userId);
      const { data: expertProfile, error: profileError } = await supabase
        ?.from('expert_profiles')
        ?.select('id')
        ?.eq('user_id', userId)
        ?.single();

      console.log('📋 Expert profile lookup result:', { expertProfile, profileError });

      if (profileError || !expertProfile) {
        console.error('❌ Expert profile not found:', profileError);
        return { success: true, contracts: [] };
      }

      queryColumn = 'expert_id';
      queryValue = expertProfile.id;
      console.log('✅ Using expert_profile.id for query:', queryValue);
    }
    
    console.log('🔍 Querying marketplace_contracts with:', { queryColumn, queryValue });
    const { data, error } = await supabase
      ?.from('marketplace_contracts')
      ?.select(`
        *,
        expert:expert_profiles!expert_id(full_name, user_id),
        case:cases(id, case_number, property_address, allegation_type, status)
      `)
      ?.eq(queryColumn, queryValue)
      ?.order('created_at', { ascending: false });

    console.log('📋 Contracts query result (before client lookup):', { data, error, count: data?.length });

    // Fetch client info via SECURITY DEFINER RPC — bypasses RLS safely
    if (data && data.length > 0) {
      const clientIds = [...new Set(data.map(c => c.client_id))];
      console.log('🔍 Fetching client profiles for IDs:', clientIds);

      const { data: clients, error: clientError } = await supabase
        ?.rpc('get_user_basic_profiles', { p_user_ids: clientIds });

      console.log('📋 Clients fetched:', clients);

      if (clients && !clientError) {
        const clientMap = Object.fromEntries(clients.map(c => [c.id, c]));
        data.forEach(contract => {
          contract.client = clientMap[contract.client_id] || null;
        });
      }
    }

    console.log('📋 Final contracts with client data:', { data, count: data?.length });

    if (error) throw error;

    return { success: true, contracts: data || [] };
  } catch (error) {
    console.error('❌ Get user contracts error:', error);
    return { success: false, error: error?.message };
  }
}

/**
 * Create time entry
 */
export async function createTimeEntry(timeEntryData) {
  try {
    const { contractId, expertId, hours, description, workDate, hourlyRate } = timeEntryData;
    
    // Calculate billable amount
    const billableAmount = hourlyRate * hours;

    const { data, error } = await supabase
      ?.from('time_entries')
      ?.insert({
        contract_id: contractId,
        expert_id: expertId,
        work_date: workDate,
        minutes: hours * 60,
        description,
        hourly_rate: hourlyRate,
        billable_amount: billableAmount,
        status: 'approved',
        approved_at: new Date().toISOString()
      })
      ?.select()
      ?.single();

    if (error) throw error;

    // Fire-and-forget: notify the client by email
    supabase?.functions?.invoke('send-time-entry-notification', {
      body: { timeEntryId: data?.id, contractId }
    }).catch(() => {});

    return { success: true, timeEntry: data };
  } catch (error) {
    console.error('Create time entry error:', error);
    return { success: false, error: error?.message };
  }
}

/**
 * Approve time entry
 */
export async function approveTimeEntry(timeEntryId) {
  try {
    const { data, error } = await supabase
      ?.from('time_entries')
      ?.update({
        status: 'approved',
        approved_at: new Date()?.toISOString()
      })
      ?.eq('id', timeEntryId)
      ?.select()
      ?.single();

    if (error) throw error;

    logAuditEvent({ actionType: 'contract.time_entry_approved', actionDescription: `Time entry approved`, entityType: 'time_entry', entityId: timeEntryId, severity: 'info' }).catch(console.error);
    return { success: true, timeEntry: data };
  } catch (error) {
    console.error('Approve time entry error:', error);
    return { success: false, error: error?.message };
  }
}

/**
 * Get a single contract with milestones, time entries, and client info
 */
export async function getContractWithDetails(contractId) {
  try {
    const { data, error } = await supabase
      ?.from('marketplace_contracts')
      ?.select(`
        *,
        expert:expert_profiles!expert_id(id, full_name, user_id, hourly_rate, title, avatar_url),
        milestones:contract_milestones(*),
        time_entries:time_entries!contract_id(*),
        case:cases(id, case_number, property_address, allegation_type, status, created_at)
      `)
      ?.eq('id', contractId)
      ?.single();

    if (error) throw error;

    // Fetch client info via SECURITY DEFINER RPC
    const { data: clients } = await supabase
      ?.rpc('get_user_basic_profiles', { p_user_ids: [data.client_id] });

    return { success: true, contract: { ...data, client: clients?.[0] || null } };
  } catch (error) {
    console.error('Get contract details error:', error);
    return { success: false, error: error?.message };
  }
}

/**
 * Approve a submitted milestone (client action)
 */
export async function approveMilestone(milestoneId) {
  try {
    const { data, error } = await supabase
      ?.from('contract_milestones')
      ?.update({
        status: 'approved',
        approved_at: new Date()?.toISOString(),
      })
      ?.eq('id', milestoneId)
      ?.select()
      ?.single();

    if (error) throw error;
    logAuditEvent({ actionType: 'contract.milestone_approved', actionDescription: `Milestone approved`, entityType: 'milestone', entityId: milestoneId, severity: 'info' }).catch(console.error);
    return { success: true, milestone: data };
  } catch (error) {
    console.error('Approve milestone error:', error);
    return { success: false, error: error?.message };
  }
}

/**
 * Reject a submitted milestone (client action)
 */
export async function rejectMilestone(milestoneId, reason) {
  try {
    const { data, error } = await supabase
      ?.from('contract_milestones')
      ?.update({
        status: 'rejected',
        rejected_at: new Date()?.toISOString(),
        rejection_reason: reason,
      })
      ?.eq('id', milestoneId)
      ?.select()
      ?.single();

    if (error) throw error;
    logAuditEvent({ actionType: 'contract.milestone_rejected', actionDescription: `Milestone rejected${reason ? ': ' + reason : ''}`, entityType: 'milestone', entityId: milestoneId, metadata: { reason }, severity: 'warning' }).catch(console.error);
    return { success: true, milestone: data };
  } catch (error) {
    console.error('Reject milestone error:', error);
    return { success: false, error: error?.message };
  }
}

/**
 * Submit a milestone for client review
 */
export async function submitMilestone(milestoneId) {
  try {
    const { data, error } = await supabase
      ?.from('contract_milestones')
      ?.update({
        status: 'submitted',
        submitted_at: new Date()?.toISOString(),
      })
      ?.eq('id', milestoneId)
      ?.select()
      ?.single();

    if (error) throw error;
    logAuditEvent({ actionType: 'contract.milestone_submitted', actionDescription: `Milestone submitted for review`, entityType: 'milestone', entityId: milestoneId, severity: 'info' }).catch(console.error);
    return { success: true, milestone: data };
  } catch (error) {
    console.error('Submit milestone error:', error);
    return { success: false, error: error?.message };
  }
}

/**
 * Reject time entry
 */
export async function rejectTimeEntry(timeEntryId, reason) {
  try {
    const { data, error } = await supabase
      ?.from('time_entries')
      ?.update({
        status: 'rejected',
        rejection_reason: reason
      })
      ?.eq('id', timeEntryId)
      ?.select()
      ?.single();

    if (error) throw error;

    logAuditEvent({ actionType: 'contract.time_entry_rejected', actionDescription: `Time entry rejected${reason ? ': ' + reason : ''}`, entityType: 'time_entry', entityId: timeEntryId, metadata: { reason }, severity: 'warning' }).catch(console.error);
    return { success: true, timeEntry: data };
  } catch (error) {
    console.error('Reject time entry error:', error);
    return { success: false, error: error?.message };
  }
}